import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { Card, Loading, Btn, EmptyState } from "@/components/primitives";
import { initials, timeAgo, fmtTime } from "@/lib/format";
import { toast } from "sonner";
import { MessageSquare, Send, Plus, ArrowLeft, Users, Loader2, X, Image as ImageIcon, MoreVertical, Trash2, Check, CheckCheck } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

function compressImage(file, maxDim = 1000, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new window.Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDim) { height = Math.round((height * maxDim) / width); width = maxDim; }
        else if (height > maxDim) { width = Math.round((width * maxDim) / height); height = maxDim; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality).split(",")[1]);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Chat() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { subscribe, focusConversation, blurConversation, sendTyping } = useNotifications();
  const [convos, setConvos] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const activeIdRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [deleteMsgId, setDeleteMsgId] = useState(null);
  const [deleteConvo, setDeleteConvo] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [image, setImage] = useState(null);
  const fileRef = useRef(null);
  const [typingName, setTypingName] = useState(null);
  const typingClear = useRef(null);
  const typingThrottle = useRef(0);

  const onType = (e) => {
    setText(e.target.value);
    const now = Date.now();
    if (activeIdRef.current && now - typingThrottle.current > 2000) {
      typingThrottle.current = now;
      sendTyping(activeIdRef.current);
    }
  };
  const [users, setUsers] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupMembers, setGroupMembers] = useState([]);
  const endRef = useRef(null);

  const loadConvos = useCallback(() => api.get("/conversations").then((r) => setConvos(r.data)).catch(() => setConvos([])), []);
  useEffect(() => { loadConvos(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, [loadConvos]);

  const active = convos?.find((c) => c.id === activeId);

  const loadMessages = useCallback(async (id, silent) => {
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      setMessages(data);
      if (!silent) api.post(`/conversations/${id}/read`, {}).then(loadConvos).catch((e) => console.error("Failed to mark conversation read:", e));
    } catch (e) {
      console.error("Failed to load messages:", e);
    }
  }, [loadConvos]);

  useEffect(() => {
    if (!activeId) return;
    activeIdRef.current = activeId;
    focusConversation(activeId);
    setTypingName(null);
    loadMessages(activeId);
    const t = setInterval(() => loadMessages(activeId, true), 10000); // fallback; WS handles real-time
    return () => { clearInterval(t); activeIdRef.current = null; blurConversation(); };
  }, [activeId, loadMessages, focusConversation, blurConversation]);

  // Real-time updates via WebSocket.
  useEffect(() => {
    const off = subscribe((evt) => {
      if (evt.type === "message.new") {
        if (evt.conversation_id === activeIdRef.current && evt.message) {
          setMessages((prev) => prev.some((x) => x.id === evt.message.id) ? prev : [...prev, evt.message]);
          api.post(`/conversations/${activeIdRef.current}/read`, {}).then(loadConvos).catch(() => {});
        } else {
          loadConvos();
        }
      } else if (evt.type === "message.deleted") {
        if (evt.conversation_id === activeIdRef.current) {
          setMessages((prev) => prev.filter((m) => m.id !== evt.message_id));
        }
        loadConvos();
      } else if (evt.type === "message.read") {
        if (evt.conversation_id === activeIdRef.current && evt.user_id && evt.user_id !== user?.id) {
          const upTo = evt.read_at ? new Date(evt.read_at).getTime() : Date.now();
          setMessages((prev) => prev.map((m) =>
            new Date(m.created_at).getTime() <= upTo && !(m.read_by || []).includes(evt.user_id)
              ? { ...m, read_by: [...(m.read_by || []), evt.user_id] }
              : m
          ));
        }
      } else if (evt.type === "conversation.new" || evt.type === "conversation.deleted") {
        if (evt.type === "conversation.deleted" && evt.conversation_id === activeIdRef.current) setActiveId(null);
        loadConvos();
      } else if (evt.type === "chat.typing") {
        if (evt.conv_id === activeIdRef.current && evt.user_id !== user?.id) {
          setTypingName(evt.user_name || "Someone");
          if (typingClear.current) clearTimeout(typingClear.current);
          typingClear.current = setTimeout(() => setTypingName(null), 3500);
        }
      }
    });
    return off;
  }, [subscribe, loadConvos, user]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if ((!text.trim() && !image) || !activeId) return;
    setSending(true);
    const body = text.trim();
    const img = image;
    setText("");
    setImage(null);
    try {
      await api.post(`/conversations/${activeId}/messages`, { text: body, image_b64: img });
      await loadMessages(activeId, true);
      loadConvos();
    } catch (err) {
      toast.error(apiError(err));
      setText(body);
      setImage(img);
    } finally {
      setSending(false);
    }
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    try { setImage(await compressImage(file)); }
    catch { toast.error("Couldn't process that image"); }
  };

  const startDm = async (uid) => {
    try {
      const { data } = await api.post(`/conversations/dm/${uid}`, {});
      setNewOpen(false);
      await loadConvos();
      setActiveId(data.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  const createGroup = async () => {
    if (!groupName.trim() || groupMembers.length === 0) { toast.error("Add a name and members"); return; }
    try {
      const { data } = await api.post("/conversations/group", { name: groupName.trim(), member_ids: groupMembers });
      setNewOpen(false); setGroupName(""); setGroupMembers([]);
      await loadConvos();
      setActiveId(data.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  const others = useMemo(() => users.filter((u) => u.id !== user?.id), [users, user?.id]);

  // Conversation delete eligibility: DMs deletable by any participant; groups by
  // creator or admin; region groups never deletable (backend returns 400).
  const canDeleteConvo = (c) => {
    if (!c || c.is_region_group) return false;
    if (c.type === "group") return c.created_by === user?.id || isAdmin;
    return true;
  };
  const convoLabel = (c) => c?.display_name || c?.name || "this conversation";
  const isRead = (m) => (m.read_by || []).some((uid) => uid && uid !== m.sender_id);

  const doDeleteMessage = async () => {
    const mid = deleteMsgId;
    setDeleteMsgId(null);
    if (!mid) return;
    try {
      await api.delete(`/messages/${mid}`);
      setMessages((prev) => prev.filter((m) => m.id !== mid));
      loadConvos();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doDeleteConvo = async () => {
    const c = deleteConvo;
    setDeleteConvo(null);
    if (!c) return;
    try {
      await api.delete(`/conversations/${c.id}`);
      if (activeId === c.id) setActiveId(null);
      await loadConvos();
      toast.success("Conversation deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (convos === null) return <Loading />;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-curtron-ink tracking-tight">Chat</h1>
        <Btn onClick={() => setNewOpen(true)} data-testid="new-chat-btn"><Plus className="w-4 h-4" /> New</Btn>
      </div>

      <Card className="overflow-hidden">
        <div className="flex flex-col h-[calc(100vh-260px)] min-h-[420px]">
          {/* Conversation list */}
          <div className={cn("flex-1 overflow-y-auto", activeId && "hidden")}>
            {convos.length === 0 ? (
              <EmptyState icon={MessageSquare} title="No conversations" subtitle="Start a new chat." />
            ) : (
              convos.map((c) => (
                <div key={c.id} className={cn("group/row w-full flex items-center border-b border-curtron-line hover:bg-curtron-bg transition-colors", activeId === c.id && "bg-curtron-bg")}>
                  <button onClick={() => setActiveId(c.id)} data-testid={`convo-${c.id}`}
                    className="flex items-center gap-3 px-4 py-3 text-left flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-curtron-moss/25 text-curtron-green flex items-center justify-center text-sm font-semibold shrink-0">
                      {c.type === "group" || c.is_region_group ? <Users className="w-4 h-4" /> : initials(c.display_name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm text-curtron-ink truncate">{c.display_name || c.name || "Conversation"}</span>
                        {c.last_message && <span className="text-[10px] text-curtron-muted shrink-0">{timeAgo(c.last_message.created_at)}</span>}
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-curtron-muted truncate">{c.last_message ? (c.last_message.has_image ? "📷 Photo" : c.last_message.text) : "No messages yet"}</span>
                        {c.unread_count > 0 && <span className="bg-curtron-terracotta text-white text-[10px] rounded-full px-1.5 py-0.5 leading-none shrink-0">{c.unread_count}</span>}
                      </div>
                    </div>
                  </button>
                  {canDeleteConvo(c) && (
                    <button onClick={() => setDeleteConvo(c)} data-testid={`convo-delete-${c.id}`} aria-label="Delete conversation"
                      className="shrink-0 p-2 mr-2 rounded-md text-curtron-muted hover:text-curtron-terracotta hover:bg-curtron-terracotta/10 opacity-0 group-hover/row:opacity-100 focus:opacity-100 transition-opacity">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Thread */}
          <div className={cn("flex flex-col min-w-0 flex-1", !activeId && "hidden")}>
            {!active ? (
              <div className="flex-1 flex items-center justify-center"><EmptyState icon={MessageSquare} title="Select a conversation" subtitle="Choose a chat to view messages." /></div>
            ) : (
              <>
                <div className="flex items-center gap-2 px-4 py-3 border-b border-curtron-line">
                  <button className="p-1" onClick={() => setActiveId(null)} data-testid="chat-back"><ArrowLeft className="w-5 h-5" /></button>
                  <div className="w-8 h-8 rounded-full bg-curtron-moss/25 text-curtron-green flex items-center justify-center text-xs font-semibold">
                    {active.type === "group" || active.is_region_group ? <Users className="w-4 h-4" /> : initials(active.display_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-curtron-ink truncate">{active.display_name || active.name}</div>
                    {active.other_user?.role && <div className="text-[11px] text-curtron-muted capitalize">{active.other_user.role}</div>}
                    {(active.type === "group" || active.is_region_group) && <div className="text-[11px] text-curtron-muted">{active.member_ids?.length || 0} members</div>}
                  </div>
                  {canDeleteConvo(active) && (
                    <button onClick={() => setDeleteConvo(active)} data-testid="chat-delete-conversation" aria-label="Delete conversation"
                      className="shrink-0 p-2 rounded-md text-curtron-muted hover:text-curtron-terracotta hover:bg-curtron-terracotta/10 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-curtron-bg/40">
                  {messages.map((m) => {
                    const mine = m.sender_id === user?.id;
                    return (
                      <div key={m.id} className={cn("group/msg flex gap-2 max-w-[80%]", mine ? "ml-auto flex-row-reverse" : "")} data-testid={`message-${m.id}`}>
                        {!mine && <div className="w-7 h-7 rounded-full bg-curtron-moss/25 text-curtron-green flex items-center justify-center text-[10px] font-semibold shrink-0 mt-auto">{initials(m.sender_name)}</div>}
                        <div>
                          {!mine && <div className="text-[11px] text-curtron-muted mb-0.5 ml-1">{m.sender_name}</div>}
                          <div className={cn("rounded-2xl px-3.5 py-2 text-sm", mine ? "bg-curtron-green text-white rounded-br-sm" : "bg-curtron-surface border border-curtron-line text-curtron-ink rounded-bl-sm")}>
                            {m.image_b64 && <img src={m.image_b64.startsWith("data:") ? m.image_b64 : `data:image/jpeg;base64,${m.image_b64}`} alt="" className="rounded-lg mb-1 max-h-56" />}
                            {m.text && <span className="whitespace-pre-wrap break-words">{m.text}</span>}
                          </div>
                          <div className={cn("text-[10px] text-curtron-muted mt-0.5 flex items-center gap-1", mine ? "justify-end mr-1" : "ml-1")}>
                            {fmtTime(m.created_at)}
                            {mine && (isRead(m)
                              ? <CheckCheck className="w-3.5 h-3.5 text-curtron-info" data-testid={`msg-read-${m.id}`} />
                              : <Check className="w-3.5 h-3.5 text-curtron-muted" data-testid={`msg-sent-${m.id}`} />)}
                          </div>
                        </div>
                        {mine && (
                          <button onClick={() => setDeleteMsgId(m.id)} data-testid={`message-delete-${m.id}`} aria-label="Delete message"
                            className="self-center p-1 rounded text-curtron-muted hover:text-curtron-terracotta opacity-0 group-hover/msg:opacity-100 focus:opacity-100 transition-opacity">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>

                <form onSubmit={send} className="p-3 border-t border-curtron-line bg-curtron-surface">
                  {typingName && (
                    <div className="text-xs text-curtron-green mb-1.5 flex items-center gap-1" data-testid="chat-typing-indicator">
                      <span className="flex gap-0.5">
                        <span className="w-1 h-1 rounded-full bg-curtron-green animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1 h-1 rounded-full bg-curtron-green animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1 h-1 rounded-full bg-curtron-green animate-bounce" style={{ animationDelay: "300ms" }} />
                      </span>
                      {typingName} is typing…
                    </div>
                  )}
                  {image && (
                    <div className="relative inline-block mb-2" data-testid="chat-image-preview">
                      <img src={`data:image/jpeg;base64,${image}`} alt="attachment" className="h-20 rounded-lg border border-curtron-line" />
                      <button type="button" onClick={() => setImage(null)} data-testid="chat-image-remove"
                        className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-curtron-terracotta text-white flex items-center justify-center shadow"><X className="w-3 h-3" /></button>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input ref={fileRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" data-testid="chat-image-input" />
                    <button type="button" onClick={() => fileRef.current?.click()} data-testid="chat-attach-btn"
                      className="w-10 h-10 rounded-full border border-curtron-line text-curtron-sub flex items-center justify-center hover:bg-curtron-bg transition-colors shrink-0"><ImageIcon className="w-4 h-4" /></button>
                    <input value={text} onChange={onType} data-testid="chat-input" placeholder="Type a message…"
                      className="flex-1 h-10 px-3 rounded-full border border-curtron-line bg-curtron-bg text-sm outline-none focus:border-curtron-green focus:bg-curtron-surface transition-colors" />
                    <button type="submit" disabled={sending || (!text.trim() && !image)} data-testid="chat-send-btn"
                      className="w-10 h-10 rounded-full bg-curtron-green text-white flex items-center justify-center hover:bg-curtron-greenHover transition-colors disabled:opacity-50 shrink-0">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </Card>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Conversation</DialogTitle>
            <DialogDescription>Start a direct message or create a group.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.05em] font-medium text-curtron-sub mb-2">Direct Message</div>
              <div className="space-y-1 max-h-48 overflow-y-auto border border-curtron-line rounded-md">
                {others.map((u) => (
                  <button key={u.id} onClick={() => startDm(u.id)} data-testid={`dm-user-${u.id}`} className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-curtron-bg transition-colors">
                    <div className="w-8 h-8 rounded-full bg-curtron-moss/25 text-curtron-green flex items-center justify-center text-xs font-semibold">{initials(u.name)}</div>
                    <div><div className="text-sm font-medium text-curtron-ink">{u.name}</div><div className="text-[11px] text-curtron-muted capitalize">{u.role}</div></div>
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-curtron-line pt-4">
              <div className="text-[11px] uppercase tracking-[0.05em] font-medium text-curtron-sub mb-2">New Group</div>
              <input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Group name" data-testid="group-name"
                className="w-full h-10 px-3 rounded-md border border-curtron-line bg-curtron-surface text-sm outline-none focus:border-curtron-green mb-2" />
              <div className="flex flex-wrap gap-1.5 mb-2">
                {groupMembers.map((id) => {
                  const u = users.find((x) => x.id === id);
                  return <span key={id} className="inline-flex items-center gap-1 bg-curtron-green/10 text-curtron-green text-xs rounded-full px-2 py-1">{u?.name}<button onClick={() => setGroupMembers((m) => m.filter((x) => x !== id))}><X className="w-3 h-3" /></button></span>;
                })}
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto border border-curtron-line rounded-md">
                {others.filter((u) => !groupMembers.includes(u.id)).map((u) => (
                  <button key={u.id} onClick={() => setGroupMembers((m) => [...m, u.id])} className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-curtron-bg transition-colors text-sm">
                    <div className="w-7 h-7 rounded-full bg-curtron-moss/25 text-curtron-green flex items-center justify-center text-[10px] font-semibold">{initials(u.name)}</div>{u.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setNewOpen(false)}>Cancel</Btn>
            <Btn onClick={createGroup} data-testid="create-group-btn"><Users className="w-4 h-4" /> Create Group</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteMsgId} onOpenChange={(v) => !v && setDeleteMsgId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>This removes the message for everyone in the conversation. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="message-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteMessage} data-testid="message-delete-confirm" className="bg-curtron-terracotta hover:bg-[#A35549]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteConvo} onOpenChange={(v) => !v && setDeleteConvo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteConvo?.type === "group" ? `Delete group '${convoLabel(deleteConvo)}'?` : `Delete conversation with ${convoLabel(deleteConvo)}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the conversation and its messages. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="convo-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteConvo} data-testid="convo-delete-confirm" className="bg-curtron-terracotta hover:bg-[#A35549]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
